import React from 'react';
import { Box, Button, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import UploadRow from './UploadRow';
import RestorableUploadRow from './RestorableUploadRow';
import ChequeUploadRow from './ChequeUploadRow';
import RestorableChequeUploadRow from './RestorableChequeUploadRow';
import BankDetailsDialog from './BankDetailsDialog';

const DocumentUploadForm = ({
  formData,
  files,
  fileLinks,
  details,
  onFileUpload,
  onPreview,
  hasNoCheque,
  onChequeCheckboxChange,
  bankDetails,
  onBankInputChange,
  bankDialogOpen,
  onBankDialogClose,
  onBankDialogOpen,
  altDocType,
  onAltDocTypeChange,
  mandateFile,
  manualTanNumber,
  onTanNumberChange
}) => {
  const { statusOfCustomer, gstStatus } = formData;
  const isNormalRegistered = gstStatus === 'Normal Registered';

  // Helper function to render either UploadRow or RestorableUploadRow
  const renderUploadRow = (label, fileType, isOptional = false) => {
    const file = files[`${fileType}File`];
    const fileLink = fileLinks[`${fileType}FileLink`];
    const handler = onFileUpload(fileType);
    
    // Extract file name from the URL or use a default name
    const getFileNameFromUrl = (url, type) => {
      if (!url) return null;
      const urlParts = url.split('/');
      const fileName = urlParts[urlParts.length - 1];
      return fileName.includes('.') ? fileName : `${type}-document.pdf`;
    };
    
    // Determine if this is a TAN certificate upload
    const isTanCertificate = fileType === 'tan';
    const tanValue = isTanCertificate ? (manualTanNumber || details.tanDetails?.tanNumber || '') : '';
    
    // If we have a restored file link but no current file, use RestorableUploadRow
    if (fileLink && !file) {
      return (
        <RestorableUploadRow
          key={fileType}
          label={label}
          file={file}
          fileLink={fileLink}
          handler={handler}
          isOptional={isOptional}
          onPreview={onPreview}
          fileName={getFileNameFromUrl(fileLink, fileType)}
          tanValue={isTanCertificate ? tanValue : undefined}
          onTanChange={isTanCertificate ? onTanNumberChange : undefined}
        />
      );
    }
    
    // Otherwise use regular UploadRow
    return (
      <UploadRow
        key={fileType}
        label={label}
        file={file}
        handler={handler}
        isOptional={isOptional}
        onPreview={onPreview}
        tanValue={isTanCertificate ? tanValue : undefined}
        onTanChange={isTanCertificate ? onTanNumberChange : undefined}
      />
    );
  };

  // Helper function to render cheque upload with restoration support
  const renderChequeUploadRow = () => {
    const chequeFile = files.chequeFile;
    const chequeFileLink = fileLinks.chequeFileLink;
    
    // If we have a restored cheque file link but no current file, use RestorableChequeUploadRow
    if (chequeFileLink && !chequeFile) {
      return (
        <RestorableChequeUploadRow
          chequeFile={chequeFile}
          chequeFileLink={chequeFileLink}
          onChequeUpload={onFileUpload('cheque')}
          hasNoCheque={hasNoCheque}
          onCheckboxChange={onChequeCheckboxChange}
          bankDetails={bankDetails}
          onBankDialogOpen={onBankDialogOpen}
          onPreview={onPreview}
        />
      );
    }
    
    // Otherwise use regular ChequeUploadRow
    return (
      <ChequeUploadRow
        chequeFile={chequeFile}
        onChequeUpload={onFileUpload('cheque')}
        hasNoCheque={hasNoCheque}
        onCheckboxChange={onChequeCheckboxChange}
        bankDetails={bankDetails}
        onBankDialogOpen={onBankDialogOpen}
        onPreview={onPreview}
      />
    );
  };

  const renderDeclarationSection = () => (
    <>
      {renderUploadRow("Declaration Certificate (GST-Unregistered)", "declaration")}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          href="/declaration_template.pdf"
          target="_blank"
          rel="noopener noreferrer"
          download
        >
          Download Declaration Form Template
        </Button>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          Please download, fill, sign, and upload the scanned copy above.
        </Typography>
      </Box>
    </>
  );

  const renderAltDocSection = () => (
    <Box sx={{ mb: 3 }}>
      <FormControl fullWidth required>
        <InputLabel>Type of Document</InputLabel>
        <Select
          label="Type of Document"
          value={altDocType}
          onChange={onAltDocTypeChange}
        >
          <MenuItem value="passport">Passport</MenuItem>
          <MenuItem value="dl">Driver's License</MenuItem>
          <MenuItem value="voter_id">Voter ID</MenuItem>
        </Select>
      </FormControl>
      {altDocType && (
        <Box sx={{ mt: 2 }}>
          {renderUploadRow(
            altDocType === 'passport' ? 'Passport' : altDocType === 'dl' ? "Driver's License" : 'Voter ID',
            'altDoc'
          )}
        </Box>
      )}
    </Box>
  );

  const renderProprietorIndividual = () => {
    if (isNormalRegistered) {
      return (
        <>
          {renderUploadRow("GST Certificate", "gst")}
          {renderUploadRow("PAN Card", "pan")}
          {renderUploadRow("TAN Certificate (Optional) - Proof of TAN number should be legitimate", "tan", true)}
          {renderUploadRow("Certificate of Incorporation (if any)", "inc")}
          {renderChequeUploadRow()}
        </>
      );
    } else {
      return (
        <>
          {renderDeclarationSection()}
          {renderUploadRow("PAN Card", "pan")}
          {renderUploadRow("TAN Certificate (Optional) - Proof of TAN number should be legitimate", "tan", true)}
          {renderChequeUploadRow()}
          {renderAltDocSection()}
        </>
      );
    }
  };

  const renderLLP = () => {
    if (isNormalRegistered) {
      return (
        <>
          {renderUploadRow("GST Certificate", "gst")}
          {renderUploadRow("PAN Card", "pan")}
          {renderUploadRow("TAN Certificate - Proof of TAN number should be legitimate", "tan")}
          {renderUploadRow("Certificate of Incorporation", "inc")}
          {renderChequeUploadRow()}
        </>
      );
    } else {
      return (
        <>
          {renderDeclarationSection()}
          {renderUploadRow("PAN Card", "pan")}
          {renderUploadRow("TAN Certificate (Optional) - Proof of TAN number should be legitimate", "tan", true)}
          {renderUploadRow("Certificate of Incorporation", "inc")}
          {/* {renderUploadRow("Certificate of Incorporation", "inc")} */}
          {renderChequeUploadRow()}
        </>
      );
    }
  };

  const renderRegisteredPartnership = () => {
    if (isNormalRegistered) {
      return (
        <>
          {renderUploadRow("GST Certificate", "gst")}
          {renderUploadRow("PAN Card", "pan")}
          {renderUploadRow("TAN Certificate - Proof of TAN number should be legitimate", "tan")}
          {renderUploadRow("Partnership Deed", "partnership")}
          {renderChequeUploadRow()}
        </>
      );
    } else {
      return (
        <>
          {renderDeclarationSection()}
          {renderUploadRow("PAN Card", "pan")}
          {renderUploadRow("TAN Certificate (Optional) - Proof of TAN number should be legitimate", "tan", true)}
          {renderUploadRow("Certificate of Incorporation", "inc")}
          {renderUploadRow("Partnership Deed", "partnership")}
          {renderChequeUploadRow()}
        </>
      );
    }
  };

  const renderUnregisteredPartnership = () => {
    if (isNormalRegistered) {
      return (
        <>
          {renderUploadRow("GST Certificate", "gst")}
          {renderUploadRow("PAN Card", "pan")}
          {renderUploadRow("TAN Certificate (Optional) - Proof of TAN number should be legitimate", "tan", true)}
          {renderChequeUploadRow()}
        </>
      );
    } else {
      return (
        <>
          {renderDeclarationSection()}
          {renderUploadRow("PAN Card", "pan")}
          {renderUploadRow("TAN Certificate (Optional) - Proof of TAN number should be legitimate", "tan", true)}
          {renderUploadRow("Certificate of Incorporation", "inc")}
          {renderChequeUploadRow()}
        </>
      );
    }
  };

  const renderPrivateLimited = () => {
    if (isNormalRegistered) {
      return (
        <>
          {renderUploadRow("GST Certificate", "gst")}
          {renderUploadRow("PAN Card", "pan")}
          {renderUploadRow("TAN Certificate - Proof of TAN number should be legitimate", "tan")}
          {renderUploadRow("Certificate of Incorporation", "inc")}
          {renderChequeUploadRow()}
        </>
      );
    } else {
      return (
        <>
          {renderDeclarationSection()}
          {renderUploadRow("PAN Card", "pan")}
          {renderUploadRow("TAN Certificate (Optional) - Proof of TAN number should be legitimate", "tan", true)}
          {renderUploadRow("Certificate of Incorporation", "inc")}
          {renderChequeUploadRow()}
        </>
      );
    }
  };

  const renderSociety = () => {
    if (isNormalRegistered) {
      return (
        <>
          {renderUploadRow("GST Certificate", "gst")}
          {renderUploadRow("PAN Card", "pan")}
          {renderUploadRow("TAN Certificate - Proof of TAN number should be legitimate", "tan")}
          {renderChequeUploadRow()}
        </>
      );
    } else {
      return (
        <>
          {renderDeclarationSection()}
          {renderUploadRow("PAN Card", "pan")}
          {renderUploadRow("TAN Certificate (Optional) - Proof of TAN number should be legitimate", "tan", true)}
          {renderUploadRow("Certificate of Incorporation", "inc")}
          {renderChequeUploadRow()}
        </>
      );
    }
  };

  const renderForeignEntity = () => (
    <>
      {renderUploadRow("Declaration Certificate", "declaration", true)}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          href="/declaration_template.pdf"
          target="_blank"
          rel="noopener noreferrer"
          download
        >
          Download Declaration Form Template
        </Button>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          Please download, fill, sign, and upload the scanned copy above.
        </Typography>
      </Box>
      {renderUploadRow("Swift Advice Copy", "swift")}
      {renderUploadRow("PAN Card", "pan", true)}
      {renderUploadRow("Certificate of Incorporation", "inc", true)}
      {renderUploadRow("Address Proof", "address", true)}
    </>
  );

  const renderDocuments = () => {
    switch (statusOfCustomer) {
      case 'Proprietor / Individual':
        return renderProprietorIndividual();
      case 'LLP':
        return renderLLP();
      case 'Registered Partnership':
        return renderRegisteredPartnership();
      case 'Unregistered Partnership':
        return renderUnregisteredPartnership();
      case 'Private Limited/ Public Limited':
        return renderPrivateLimited();
      case 'Society/AOP/HUF/Trust':
        return renderSociety();
      case 'Foreign Entity':
        return renderForeignEntity();
      default:
        return null;
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      {renderDocuments()}
      <BankDetailsDialog
        open={bankDialogOpen}
        onClose={onBankDialogClose}
        bankDetails={bankDetails}
        onBankInputChange={onBankInputChange}
        hasNoCheque={hasNoCheque}
        mandateFile={mandateFile}
        mandateFileLink={fileLinks.mandateFileLink}
        onMandateUpload={onFileUpload('mandate')}
        onPreview={onPreview}
      />
    </Box>
  );
};

export default DocumentUploadForm;